import type {
  InspectionMessage,
  InspectionTurn,
} from '../../services/roomInspectorApi';
import { chronologicalMessages } from './messageMetadata.ts';

export interface TranscriptTurnBlock {
  id: string;
  turnId: string;
  continuation: boolean;
  messages: InspectionMessage[];
  processMessages: InspectionMessage[];
  callCount: number;
  toolResultCount: number;
  thinkingCount: number;
  eventCount: number;
  turn?: InspectionTurn;
  deliverResult?: unknown;
}
export function messageText(payload: unknown): string {
  if (typeof payload === 'string') return payload;
  if (Array.isArray(payload))
    return payload.map(messageText).filter(Boolean).join('\n');
  if (!payload || typeof payload !== 'object') return '';
  const item = payload as Record<string, unknown>;
  if (typeof item.text === 'string') return item.text;
  if (typeof item.content === 'string' || Array.isArray(item.content))
    return messageText(item.content);
  if (typeof item.message === 'string') return item.message;
  return '';
}
export function isTool(kind: string): boolean {
  return /^(tool_call|tool_result|tool_call_output|function_call|function_call_output|custom_tool_call|custom_tool_call_output)$/.test(
    kind,
  );
}
function isCall(kind: string): boolean {
  return isTool(kind) && !/result|output/.test(kind);
}
export function isThinking(kind: string): boolean {
  return /thinking|reasoning/.test(kind);
}

export function isProcessMessage(message: InspectionMessage): boolean {
  return (
    message.role !== 'user' &&
    (isTool(message.kind) ||
      isThinking(message.kind) ||
      !messageText(message.payload))
  );
}

// Match Tutti's chronological Turn presentation: turnless session events may
// stay between rows of the same Turn, without changing their source turnId.
// A different Turn starts a new section, even when both contain only tools.
export function groupMessages(
  messages: InspectionMessage[],
  turns: InspectionTurn[],
): TranscriptTurnBlock[] {
  const ordered = chronologicalMessages(messages);
  const groups: TranscriptTurnBlock[] = [];
  const turnById = new Map(turns.map((turn) => [turn.turnId, turn]));
  const lastByTurn = new Map<string, TranscriptTurnBlock>();
  const nextTurnIds = new Array<string>(ordered.length);
  let nextTurnId = '';
  for (let index = ordered.length - 1; index >= 0; index--) {
    nextTurnIds[index] = nextTurnId;
    if (ordered[index].turnId) nextTurnId = ordered[index].turnId;
  }
  let current: TranscriptTurnBlock | undefined;
  for (const [index, message] of ordered.entries()) {
    const turnId =
      message.turnId ||
      (current?.turnId && nextTurnIds[index] === current.turnId
        ? current.turnId
        : '');
    if (
      !current ||
      current.turnId !== turnId ||
      (!turnId && message.role === 'user' && current.messages.length > 0)
    ) {
      const previous = turnId ? lastByTurn.get(turnId) : undefined;
      current = {
        id: turnId
          ? previous
            ? `${turnId}:continuation:${message.messageId}`
            : turnId
          : `unassigned:${message.messageId}`,
        turnId,
        continuation: Boolean(previous),
        messages: [],
        processMessages: [],
        callCount: 0,
        toolResultCount: 0,
        thinkingCount: 0,
        eventCount: 0,
        turn: turnId ? turnById.get(turnId) : undefined,
      };
      groups.push(current);
      if (turnId) lastByTurn.set(turnId, current);
    }
    current.messages.push(message);
    if (isProcessMessage(message)) {
      current.processMessages.push(message);
      current.callCount += Number(isCall(message.kind));
      current.toolResultCount += Number(
        isTool(message.kind) && !isCall(message.kind),
      );
      current.thinkingCount += Number(isThinking(message.kind));
      current.eventCount += Number(
        !isTool(message.kind) && !isThinking(message.kind),
      );
    }
  }
  for (const group of groups) {
    const result = group.turn?.deliverResult;
    if (
      group.turnId &&
      lastByTurn.get(group.turnId) === group &&
      result &&
      typeof result === 'object' &&
      Object.keys(result).length > 0
    )
      group.deliverResult = result;
  }
  return groups;
}

export function mergeById<T>(
  older: T[],
  newer: T[],
  id: (item: T) => string,
): T[] {
  const merged = new Map(older.map((item) => [id(item), item]));
  for (const item of newer) merged.set(id(item), item);
  return [...merged.values()];
}
